
























































































































































































































































































import { Component, Vue, Watch } from "vue-property-decorator";
import PhoneEditingHintComponent from "@/components/admin/PhoneEditingHint.vue";
import { firestore } from "firebase";
import router from "@/router/router";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { ComponentName } from "@/utils/RouteGuards";
import {
  CURRENT_RENO_MODEL_VERSION,
  renoReviewerKey,
  RENO_RATING_MIN,
  RenoReviewDeviceType,
  RenoReviewFilters,
  emptyReviewFilters,
  formatFilterDate,
} from "@/data/models/RenoReviewEntry";
import {
  countReviewQueue,
  loadAvailableDeviceTypes,
} from "@/data/repository/RenoReviewRepository";
import { getActiveUserId } from "@/data/repository/UserRepository";
import {
  getClientId,
  getReviewableClients,
} from "@/data/repository/ClientRepository";
import RenoReviewRatingComponent from "@/components/admin/renoReview/RenoReviewRating.vue";
import DatePickerComponent from "@/components/common/DatePicker.vue";
import {
  COLOR_TABLE_HEADER,
  COLOR_TABLE_HEADER_DARK,
} from "@/utils/colors";
import { themed } from "@/utils/Theme";
import { formatNumber } from "@/utils/Utils";
import ToolbarOverflowItemComponent from "@/components/common/ToolbarOverflowItem.vue";

/**
 * How long to wait after the last filter change before counting.
 *
 * The count reads up to a thousand queue documents plus the reviewer's own stores, and the serial
 * field re-counts on every keystroke, so counting immediately meant a full scan per character typed.
 */
const COUNT_DEBOUNCE_MS = 400;

/**
 * How far back "Ab Datum" starts when it is first chosen.
 *
 * The same span as the months option's default, so switching between the two ways of expressing one
 * bound does not change the result until the reviewer actually picks a date. An empty date would leave
 * the option selected while bounding nothing.
 */
const DEFAULT_FROM_DATE_MONTHS = 12;

/**
 * Queue screen for the RENO prediction review.
 *
 * Picks the filters and reports roughly how much work they imply, then hands off to the stepper.
 * The filters travel as route query parameters so a review pass survives a refresh and can be shared
 * as a link.
 */
@Component({
  components: {
    "toolbar-overflow-item": ToolbarOverflowItemComponent,
    "reno-review-rating": RenoReviewRatingComponent,
    "date-picker": DatePickerComponent,
    "phone-editing-hint": PhoneEditingHintComponent,
  },
})
export default class RenoReviewQueueComponent extends Vue {
  /** Below 600 px: the hint instead of PRÜFUNG STARTEN (PhoneEditingHint). */
  get isPhone(): boolean {
    return this.$vuetify.breakpoint.xsOnly;
  }

  deviceTypes: RenoReviewDeviceType[] = [];
  startAtSerial = "";
  /**
   * `none` | `age` | `from` | `active` — one control, because all four compete for the single
   * range/equality slot a query has.
   */
  periodMode = "none";
  maxAgeMonths = 12;
  /** Start of the chosen day, as the shared picker hands it over. */
  fromDate: firestore.Timestamp | null = null;

  /** Owning client to restrict the pass to, i.e. whose drying it was. */
  clientId: string | null = null;
  /** Client id to display name, loaded on first use of the picker. */
  clientNamesById: Record<string, string> = {};
  isLoadingClients = false;
  private hasLoadedClients = false;

  /** Keep only dryings this reviewer marked for checking. */
  onlyChainSuspect = false;
  /** Keep only dryings this reviewer commented on. */
  onlyWithComment = false;
  /**
   * Keep only dryings this reviewer hid, instead of subtracting them.
   *
   * Off is the ordinary behaviour: a hide that a filter switches off by default would not be a hide.
   * On, it is the one pass that shows them, which is what makes the stepper's hide toggle reversible.
   */
  onlySuppressed = false;

  /**
   * `all` | `unrated` | `only` — one control, because "skip what I rated" and "only what I rated"
   * are opposite requests and must not both be expressible at once.
   */
  ratingMode = "all";

  /** Which of this reviewer's own scores to revisit, when `ratingMode === "only"`. */
  onlyRatings: number[] = [RENO_RATING_MIN, RENO_RATING_MIN + 1];

  /** Skip dryings merely stepped past. Off by default, per D10: an accidental Weiter must not hide one. */
  hideSeen = false;

  /** Device types present in the index. Empty until the probe returns, or if it failed. */
  availableDeviceTypes: RenoReviewDeviceType[] = [];

  /** Dryings this pass will show, after every filter. */
  dryingCount = 0;
  /** The scan hit its cap, so the figure is a lower bound. */
  isCapped = false;
  /** An exclusion store hit its cap, so the figure may be slightly high. */
  isApproximate = false;
  isCounting = false;

  /** Guards a stale response from overwriting a newer one when filters change quickly. */
  private countToken = 0;
  /** Pending debounced count, cleared on destroy so a closed screen never queries. */
  private countTimer: number | null = null;

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /**
   * Only the device types the index actually holds.
   *
   * Built from the data, not the enum: every indexed device is currently `BOX`, so an enum-derived
   * list offered five options that guaranteed an empty queue. Falls back to the enum if the probe
   * failed, because a filter that cannot be set at all is worse than one offering too much.
   */
  get deviceTypeItems() {
    const available =
      this.availableDeviceTypes.length > 0
        ? this.availableDeviceTypes
        : Object.values(RenoReviewDeviceType);

    return available.map((value) => ({ text: value, value }));
  }

  /**
   * Clients to choose from, by display name.
   *
   * Includes the selected id even before the list has loaded, so a shared link with a client already
   * chosen does not render a blank picker.
   */
  get clientItems() {
    const items = Object.keys(this.clientNamesById).map((id) => ({
      text: this.clientNamesById[id],
      value: id,
    }));

    if (this.clientId && this.clientNamesById[this.clientId] === undefined) {
      items.unshift({ text: this.clientId, value: this.clientId });
    }

    return items;
  }

  /**
   * Loads the client list, once.
   *
   * Started on mount and not awaited: it is a few hundred documents, so the picker may briefly show a
   * spinner, but the alternative of loading when the menu opens swapped `items` under an open list.
   */
  async loadClients() {
    if (this.hasLoadedClients) {
      return;
    }

    this.hasLoadedClients = true;
    this.isLoadingClients = true;

    try {
      const names = await getReviewableClients();

      names.forEach((name, id) => Vue.set(this.clientNamesById, id, name));
    } finally {
      this.isLoadingClients = false;
    }
  }

  /**
   * Adds or removes one score from the revisit selection.
   *
   * The shared scale reports a press rather than a new selection, because in the stepper a press is an
   * action. Toggling is this screen's meaning for it.
   */
  onToggleRating(value: number) {
    this.onlyRatings = this.onlyRatings.includes(value)
      ? this.onlyRatings.filter((rating) => rating !== value)
      : [...this.onlyRatings, value].sort();
  }

  get monthItems() {
    const unit = this.$t("admin.renoReview.monthsSuffix").toString();

    return [1, 3, 6, 12, 24, 36].map((months) => ({
      text: `${months} ${unit}`,
      value: months,
    }));
  }

  get filters(): RenoReviewFilters {
    return {
      ...emptyReviewFilters(CURRENT_RENO_MODEL_VERSION),
      deviceTypes: this.deviceTypes,
      maxAgeMonths: this.periodMode === "age" ? this.maxAgeMonths : null,
      fromDate:
        this.periodMode === "from" ? this.fromDate?.toDate() ?? null : null,
      activeOnly: this.periodMode === "active",
      clientId: this.clientId ?? null,
      onlyChainSuspect: this.onlyChainSuspect,
      onlyWithComment: this.onlyWithComment,
      onlySuppressed: this.onlySuppressed,
      hideRated: this.ratingMode === "unrated",
      // A rated drying is also a seen one, so hiding seen would empty a revisit pass entirely.
      hideSeen: this.ratingMode === "only" ? false : this.hideSeen,
      // `?? []` because deselecting every cell leaves the toggle empty, and an empty rating list has
      // to mean "no restriction" — a nullish one would reach Firestore as `in []`, which it rejects.
      onlyRatings: this.ratingMode === "only" ? this.onlyRatings ?? [] : [],
    };
  }

  /** Today, as the picker's upper bound: a drying cannot start in the future. */
  get today(): firestore.Timestamp {
    return firestore.Timestamp.fromDate(new Date());
  }

  /**
   * Fills in a starting date the first time "Ab Datum" is chosen.
   *
   * Local midnight, matching how the filter and its route parameter read a date: the bound is the start
   * of the chosen day, not the moment the reviewer clicked.
   */
  @Watch("periodMode")
  onPeriodModeChanged(mode: string) {
    if (mode !== "from" || this.fromDate) {
      return;
    }

    const start = new Date();

    start.setMonth(start.getMonth() - DEFAULT_FROM_DATE_MONTHS);
    start.setHours(0, 0, 0, 0);

    this.fromDate = firestore.Timestamp.fromDate(start);
  }

  /** Reviewer whose ratings, suppressions and seen records the count subtracts. */
  get reviewerKey(): string {
    return renoReviewerKey(getClientId(), getActiveUserId());
  }

  /**
   * Just the figure, so the heading beside it can stay put.
   *
   * `firebase@7.24.0` has no `count()` aggregation, so a wide pass is counted up to a cap and reads
   * "über 950" — the figure reached, not the cap, since filters are applied to the scanned window and
   * the survivors are what a lower bound should report.
   */
  get dryingCountValue(): string {
    if (this.isCounting) {
      return this.$t("admin.renoReview.countLoading").toString();
    }

    if (this.dryingCount === 0) {
      return this.$t("admin.renoReview.countNone").toString();
    }

    // Grouped per locale, so the cap reads "1.000" in German rather than "1000".
    const figure = formatNumber(this.dryingCount);

    if (this.isCapped) {
      return this.$t("admin.renoReview.countCapped", [figure]).toString();
    }

    return this.isApproximate
      ? this.$t("admin.renoReview.countApprox", [figure]).toString()
      : figure;
  }

  @Watch("filters", { deep: true })
  @Watch("normalisedStartAtSerial")
  onFiltersChanged() {
    this.scheduleCount();
  }

  /**
   * Debounces the count, and greys the figure out at once.
   *
   * Showing the previous number while a new one is on its way would present it as the answer to the
   * filter the reviewer has just chosen.
   */
  private scheduleCount() {
    this.isCounting = true;

    if (this.countTimer !== null) {
      window.clearTimeout(this.countTimer);
    }

    this.countTimer = window.setTimeout(() => {
      this.countTimer = null;
      this.refreshCount();
    }, COUNT_DEBOUNCE_MS);
  }

  async refreshCount() {
    const token = ++this.countToken;
    this.isCounting = true;

    try {
      const result = await countReviewQueue(
        this.reviewerKey,
        this.filters,
        this.normalisedStartAtSerial
      );

      if (token !== this.countToken) {
        return;
      }

      this.dryingCount = result.count;
      this.isCapped = result.capped;
      this.isApproximate = result.approximate;
    } catch {
      // A failed count must not block the screen; the stepper reports its own load errors.
      if (token === this.countToken) {
        this.dryingCount = 0;
        this.isCapped = false;
        this.isApproximate = false;
      }
    } finally {
      if (token === this.countToken) {
        this.isCounting = false;
      }
    }
  }

  /**
   * Serial numbers are stored uppercase, and `startAt` is a lexicographic cursor: a lowercase
   * entry sorts after every real serial and quietly returns an empty queue. Normalising here is
   * what stops a typo from looking like "no data".
   */
  get normalisedStartAtSerial(): string {
    return (this.startAtSerial ?? "").trim().toUpperCase();
  }

  /** The only route to the hide list: nothing else in the app surfaces a suppression. */
  onShowHidden() {
    router.push({ name: ComponentName.RENO_REVIEW_HIDDEN });
  }

  onStartReview() {
    router.push({
      name: ComponentName.RENO_REVIEW_STEPPER,
      query: {
        deviceTypes: (this.deviceTypes ?? []).join(","),
        startAtSerial: this.normalisedStartAtSerial,
        periodMode: this.periodMode,
        maxAgeMonths: String(this.maxAgeMonths),
        fromDate: formatFilterDate(this.filters.fromDate),
        clientId: this.filters.clientId ?? "",
        hideRated: String(this.filters.hideRated),
        hideSeen: String(this.filters.hideSeen),
        onlyRatings: this.filters.onlyRatings.join(","),
        onlyChainSuspect: String(this.filters.onlyChainSuspect),
        onlyWithComment: String(this.filters.onlyWithComment),
        onlySuppressed: String(this.filters.onlySuppressed),
      },
    });
  }

  // Access (portal client + RATE_DRYINGS) is enforced in router.beforeEach, which can await the
  // asynchronous role-settings load. A capability check here would run before that arrives.
  /**
   * Drops any chosen type the index does not actually hold.
   *
   * Only reachable from a shared link or a browser back: without it a stale `deviceTypes` query
   * parameter would silently produce an empty queue with no clue why.
   */
  private discardUnavailableSelection() {
    const available = this.deviceTypes.filter((type) =>
      this.availableDeviceTypes.includes(type)
    );

    // Only assign when something actually went. `filters` is deep-watched and rebuilt on every read,
    // so handing it a new array of the same contents still reads as a filter change and triggers
    // another count.
    if (available.length !== this.deviceTypes.length) {
      this.deviceTypes = available;
    }
  }

  async mounted() {
    ToolbarContentModule.setI18nResource("admin.renoReview.title");

    // Debounced like every other trigger, not immediate. Counting straight away meant every visit
    // counted twice: once here, and once more when the device-type probe came back and touched the
    // filters. Going through the same queue collapses them into one.
    this.scheduleCount();

    // Not awaited: the picker shows its own spinner, and nothing else on the screen depends on it.
    this.loadClients();

    try {
      this.availableDeviceTypes = await loadAvailableDeviceTypes(
        CURRENT_RENO_MODEL_VERSION
      );
      this.discardUnavailableSelection();
    } catch {
      // Leave the list empty; deviceTypeItems falls back to the enum.
    }
  }

  beforeDestroy() {
    if (this.countTimer !== null) {
      window.clearTimeout(this.countTimer);
    }
  }
}
